import { Dialog, Drawer, Popover } from './FarChat/ChatFeedbackOverlays';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  dismissWorkflowNotification,
  continueQuestionsNotificationInChat,
  listFlowWorkflowNotifications,
  listWorkflowNotifications,
  resolveWorkflowNotificationFallbackTarget,
  resolveWorkflowNotificationTarget,
  redeemWorkflowNotification,
  viewWorkflowNotificationInChat,
  type WorkflowNotification,
} from '../api/workflowNotifications';
import type { ContactConversation } from '../api/raContactChat';
import type { ContinuedQuestionsChat } from '../api/workflowNotifications';
import { ButtonBase } from '@mui/material';

export interface WorkflowNotificationsPanelProps {
  contactChatNotifications?: ContactConversation[];
  onOpenContactChat?: (conversationId: string) => void;
  open: boolean;
  onClose: () => void;
  onOpenStage: (target: {
    flowId: string;
    stageCode: number | 'ra_collaboration';
    navigationMode?: 'current_workflow';
    /** Retains the server-projected notification context for navigation. */
    kind: WorkflowNotification['kind'];
  }) => Promise<void> | void;
  /** When omitted, show the current user's global inbox. */
  flowId?: string;
  workflowName?: string;
  anchorEl?: HTMLElement | null;
  onChanged?: () => void;
  /** Preview mode may display the inbox but must never mutate notification state. */
  readOnly?: boolean;
  /** Server-derived permission gate for MR-specific notification kinds. */
  canAccessMarketResearch?: boolean;
  /** Opens a server-issued RA-owned Home Chat projection; no target route is redeemed. */
  onOpenContinuedChat?: (chat: ContinuedQuestionsChat) => Promise<void> | void;
}

const actionCopy = (item: WorkflowNotification) => (
  item.question_response_requested === true
    ? 'You are being requested to answer questions.'
    : item.question_response_requested === false ? 'A workflow was shared with you.' : ({ product_discovery_review: 'A Customer has requested your Product Discovery review.', section_c_handoff: 'A Customer has handed off Section C for your review.', section_c_handoff_requested: 'A Customer has handed off Section C for your review.', brand_name_ja: 'A Customer has handed off a Brand-name J&A for your review.' } as Partial<Record<WorkflowNotification['kind'], string>>)[item.kind] ?? 'A workflow was shared with you.'
);

const dateCopy = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? 'Recently' : date.toLocaleString();
};

/** Private recipient inbox. It deliberately accepts only already-sanitized records. */
const WorkflowNotificationsPanel: React.FC<WorkflowNotificationsPanelProps> = ({
  open,
  contactChatNotifications = [],
  onOpenContactChat,
  onClose,
  onOpenStage,
  flowId,
  workflowName,
  anchorEl,
  onChanged,
  readOnly = false,
  canAccessMarketResearch = true,
  onOpenContinuedChat,
}) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [notifications, setNotifications] = useState<WorkflowNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [openingNotificationId, setOpeningNotificationId] = useState<string | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageError, setPageError] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [dismissingNotificationId, setDismissingNotificationId] = useState<string | null>(null);
  const nextOffset = useRef(0);
  const loadedPageIds = useRef<Map<number, Set<string>>>(new Map());
  const pollPageCursor = useRef(0);
  const requestVersion = useRef(0);
  const scopeVersion = useRef(0);
  const pagePending = useRef(false);
  const actionPending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const title = flowId && workflowName ? `Notifications for ${workflowName}` : 'Notifications';

  const load = useCallback(async (offset = 0, background = false) => {
    if (!open) return;
    const append = offset > 0;
    if (append && (flowId || pagePending.current)) return;
    const version = ++requestVersion.current;
    pagePending.current = true;
    if (!background) {
      setPageError(false);
      if (append) setLoadingMore(true);
      else {
        setLoading(true);
        setLoadingMore(false);
        setLoadError(false);
        setActionError(null);
        setHasMore(false);
        setNotifications([]);
        nextOffset.current = 0;
        loadedPageIds.current.clear();
        pollPageCursor.current = 0;
      }
    }
    try {
      const result = flowId ? await listFlowWorkflowNotifications(flowId) : await listWorkflowNotifications(offset);
      if (version !== requestVersion.current) return;
      if (result.has_more && result.notifications.length === 0) throw new Error('Empty non-terminal notification page');
      const returnedIds = new Set(result.notifications.map(item => item.notification_id));
      const previousPageIds = loadedPageIds.current.get(offset) ?? new Set<string>();
      if (!flowId) {
        if (background && offset === 0 && !result.has_more) loadedPageIds.current.clear();
        loadedPageIds.current.set(offset, returnedIds);
      }
      // A background check must not rewind a cursor that loaded later pages.
      if (background && !flowId && (append || result.has_more)) {
        nextOffset.current = Math.max(nextOffset.current, offset + result.notifications.length);
      } else {
        nextOffset.current = offset + result.notifications.length;
      }
      if (!background || !append) setHasMore(!flowId && result.has_more);
      setNotifications(previous => {
        // A flow inbox is a complete server projection. A successful refresh
        // must remove records that have since become terminal or inaccessible.
        // A global background check retains other loaded pages, but removes
        // active rows omitted from the page being checked.
        const retained = append || (background && !flowId && result.has_more)
          ? previous.filter(item => !(
            background && !flowId && previousPageIds.has(item.notification_id)
            && item.execution_active === true && !returnedIds.has(item.notification_id)
          ))
          : [];
        const records = new Map(retained.map(item => [item.notification_id, item]));
        for (const item of result.notifications) records.set(item.notification_id, item);
        return Array.from(records.values());
      });
    } catch {
      if (version !== requestVersion.current || background) return;
      if (append) setPageError(true); else setLoadError(true);
    } finally {
      if (version === requestVersion.current) {
        pagePending.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [flowId, open]);

  useEffect(() => {
    scopeVersion.current += 1;
    setOpeningNotificationId(null);
    setDismissingNotificationId(null);
    actionPending.current = false;
    void load();
    const refresh = () => { void load(); };
    const refreshWhenVisible = () => { if (document.visibilityState === 'visible') void load(); };
    window.addEventListener('workflow-notifications-changed', refresh);
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.removeEventListener('workflow-notifications-changed', refresh);
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      requestVersion.current += 1;
      pagePending.current = false;
    };
  }, [load]);

  const visibleNotifications = useMemo(
    () => (canAccessMarketResearch
      ? notifications
      : notifications.filter(item => item.kind === 'workflow_shared')),
    [canAccessMarketResearch, notifications],
  );

  useEffect(() => {
    if (!open || !visibleNotifications.some((item) => item.execution_active === true)) return undefined;
    // Reconcile one loaded page containing active work per tick. This bounds
    // requests while allowing active rows beyond page zero to become terminal.
    const activeIds = new Set(visibleNotifications.filter(item => item.execution_active === true)
      .map(item => item.notification_id));
    const activeOffsets = flowId ? [0] : Array.from(loadedPageIds.current.entries())
      .filter(([, ids]) => Array.from(ids).some(id => activeIds.has(id)))
      .map(([offset]) => offset);
    const offset = activeOffsets[pollPageCursor.current % activeOffsets.length] ?? 0;
    pollPageCursor.current += 1;
    const timer = window.setTimeout(() => { void load(offset, true); }, 5_000);
    return () => window.clearTimeout(timer);
  }, [load, visibleNotifications, open]);

  const isExecutionActive = (item: WorkflowNotification) => item.execution_active === true;

  const openStage = async (item: WorkflowNotification, redeemOnOpen = item.kind !== 'questions') => {
    if (readOnly || isExecutionActive(item)) return;
    if (actionPending.current) return;
    actionPending.current = true;
    const version = scopeVersion.current;
    const isCurrent = () => version === scopeVersion.current && mounted.current;
    setActionError(null);
    setOpeningNotificationId(item.notification_id);
    try {
      const target = await resolveWorkflowNotificationTarget(item.notification_id);
      if (!isCurrent()) return;
      await onOpenStage({ ...target, kind: item.kind });
      if (version !== scopeVersion.current) return;
      if (redeemOnOpen) {
        await redeemWorkflowNotification(item.notification_id, item.version);
        if (isCurrent()) onChanged?.();
      }
      if (isCurrent()) onClose();
    } catch {
      if (!isCurrent()) return;
      setActionError('Unable to open this workflow right now. Please try again.');
    } finally {
      if (isCurrent()) {
        actionPending.current = false;
        setOpeningNotificationId(null);
      }
    }
  };

  // Continue availability is a recipient-bound server decision. In
  // particular, an old Questions/share target can be stale while the same
  // authorized RA may safely reconcile to the current 0--8 chat checkpoint.
  // Do not infer or suppress it from target readiness, kind, or local state.
  const canContinueInChat = (item: WorkflowNotification) => (
    !isExecutionActive(item) && item.can_continue_in_chat === true
  );

  const canViewInChat = (item: WorkflowNotification) => (
    isExecutionActive(item) && item.can_view_in_chat === true
  );

  const targetIsReady = (item: WorkflowNotification) => (
    !isExecutionActive(item) && item.target_ready !== false
  );

  const continueInChat = async (item: WorkflowNotification) => {
    if (readOnly || isExecutionActive(item) || !canContinueInChat(item) || !onOpenContinuedChat) return;
    if (actionPending.current) return;
    actionPending.current = true;
    const version = scopeVersion.current;
    const isCurrent = () => version === scopeVersion.current && mounted.current;
    setActionError(null);
    setOpeningNotificationId(item.notification_id);
    try {
      const requestId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `questions-chat-${Date.now()}`;
      const chat = await continueQuestionsNotificationInChat(item.notification_id, requestId);
      if (!isCurrent()) return;
      await onOpenContinuedChat(chat);
      if (isCurrent()) onClose();
    } catch (error) {
      if (!isCurrent()) return;
      const code = (error as { error_code?: unknown })?.error_code;
      // Continue races only a short server-side marker/lease boundary. Do one
      // bounded inbox reload so an active lease renders its explicit warning
      // (or an idle row becomes actionable again), rather than claiming that
      // opening the workflow failed unexpectedly.
      if (code === 'refresh_required' || code === 'workflow_active' || code === 'takeover_required') {
        await load();
        return;
      }
      if (code === 'chat_unavailable_open_workflow') {
        setActionError('Chat is not available for this workflow. Please click Open Workflow instead.');
      } else {
        setActionError('Unable to open this workflow right now. Please try again.');
      }
    } finally {
      if (isCurrent()) {
        actionPending.current = false;
        setOpeningNotificationId(null);
      }
    }
  };

  const viewInChat = async (item: WorkflowNotification) => {
    if (readOnly || !canViewInChat(item) || !onOpenContinuedChat) return;
    if (actionPending.current) return;
    actionPending.current = true;
    const version = scopeVersion.current;
    const isCurrent = () => version === scopeVersion.current && mounted.current;
    setActionError(null);
    setOpeningNotificationId(item.notification_id);
    try {
      const requestId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `workflow-observer-${Date.now()}`;
      const chat = await viewWorkflowNotificationInChat(item.notification_id, requestId);
      if (!isCurrent()) return;
      await onOpenContinuedChat(chat);
      if (isCurrent()) onClose();
    } catch {
      if (!isCurrent()) return;
      setActionError('Unable to open this workflow right now. Please try again.');
    } finally {
      if (isCurrent()) {
        actionPending.current = false;
        setOpeningNotificationId(null);
      }
    }
  };

  const openWorkflowFallback = async (item: WorkflowNotification) => {
    if (readOnly || isExecutionActive(item) || item.can_open_workflow !== true) return;
    if (actionPending.current) return;
    actionPending.current = true;
    const version = scopeVersion.current;
    const isCurrent = () => version === scopeVersion.current && mounted.current;
    setActionError(null);
    setOpeningNotificationId(item.notification_id);
    try {
      const target = await resolveWorkflowNotificationFallbackTarget(item.notification_id);
      if (!isCurrent()) return;
      await onOpenStage({ ...target, kind: item.kind });
      if (isCurrent()) onClose();
    } catch {
      if (!isCurrent()) return;
      setActionError('Unable to open this workflow right now. Please try again.');
    } finally {
      if (isCurrent()) {
        actionPending.current = false;
        setOpeningNotificationId(null);
      }
    }
  };

  const dismissItem = async (item: WorkflowNotification) => {
    if (readOnly || actionPending.current) return;
    actionPending.current = true;
    const version = scopeVersion.current;
    setDismissingNotificationId(item.notification_id);
    setActionError(null);
    const requestBeforeDismiss = requestVersion.current;
    try {
      await dismissWorkflowNotification(item.notification_id, item.version);
      if (version !== scopeVersion.current || !mounted.current) return;
      if (requestVersion.current === requestBeforeDismiss) await load();
      onChanged?.();
    } catch {
      if (version === scopeVersion.current && mounted.current) setActionError('Unable to dismiss this notification right now. Please try again.');
    } finally {
      if (version === scopeVersion.current) {
        actionPending.current = false;
        setDismissingNotificationId(null);
      }
    }
  };

  const content = (
    <Box sx={{ width: mobile ? '100%' : 430, maxWidth: '100vw', p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography id={flowId ? 'workflow-notifications-title' : undefined} component="h2" variant="h6">{title}</Typography>
        <IconButton ref={closeRef} aria-label="Close notifications" onClick={onClose}><CloseIcon /></IconButton>
      </Stack>
      <Divider sx={{ my: 1 }} />
      {!flowId && contactChatNotifications.length > 0 && onOpenContactChat && <Box component="section" aria-label="Contact chat notifications" sx={{mb:2}}>
        <Typography component="h3" variant="subtitle1">People chats</Typography>
        <List disablePadding>{contactChatNotifications.map(item => <ListItem key={item.conversation_id} disableGutters>
          <Button fullWidth sx={{textAlign:'left',justifyContent:'flex-start'}} onClick={() => { onClose(); onOpenContactChat(item.conversation_id); }}>
            <Box><Typography>Contact chat · {item.counterpart.person_name}</Typography><Typography variant="body2">{item.context.assigned_unit} · {item.context.role_id.replace(/_/g,' ')} · {item.unread_count} unread</Typography></Box>
          </Button>
        </ListItem>)}</List>
      </Box>}
      {loading ? <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress aria-label="Loading notifications" size={24} /></Box> : null}
      {!loading && loadError ? (
        <Box role="alert" sx={{ py: 2 }}>
          <Typography>Unable to load notifications right now. Please try again.</Typography>
          <Button onClick={() => void load()} aria-label="Retry notifications">Retry</Button>
        </Box>
      ) : null}
      {!loading && actionError ? (
        <Typography role="alert" sx={{ py: 1 }}>{actionError}</Typography>
      ) : null}
      {!loading && !loadError && visibleNotifications.length === 0 ? (
        <Typography sx={{ py: 2 }}>You have no workflow notifications.</Typography>
      ) : null}
      {!loading && !loadError && visibleNotifications.length > 0 ? (
        <List disablePadding aria-label={title}>
          {visibleNotifications.map((item) => (
            <ListItem key={item.notification_id} alignItems="flex-start" divider sx={{ px: 0, display: 'block' }}>
              {canContinueInChat(item) && !readOnly && onOpenContinuedChat ? (
                <ButtonBase
                  component="div"
                  onClick={() => void continueInChat(item)}
                  aria-label={`Open ${item.kind === 'questions' ? 'Questions' : 'workflow'} notification in chat for ${item.workflow_name}`}
                  sx={{ display: 'block', width: '100%', textAlign: 'left', borderRadius: 1, '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.light' } }}
                >
                  <ListItemText
                    primary={item.workflow_name}
                    secondary={<>
                      <Typography component="span" display="block" variant="body2">{actionCopy(item)}</Typography>
                      <Typography component="time" dateTime={item.created_at} display="block" variant="caption">{dateCopy(item.created_at)}</Typography>
                    </>}
                  />
                </ButtonBase>
              ) : (
                <ListItemText
                  primary={item.workflow_name}
                  secondary={<>
                    <Typography component="span" display="block" variant="body2">{actionCopy(item)}</Typography>
                    <Typography component="time" dateTime={item.created_at} display="block" variant="caption">{dateCopy(item.created_at)}</Typography>
                  </>}
                />
              )}
              {isExecutionActive(item) ? (
                <Typography role="status" aria-live="polite" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {item.active_work_message || 'This workflow is running.'}
                </Typography>
              ) : null}
              {!readOnly && <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                {canViewInChat(item) && onOpenContinuedChat ? (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={openingNotificationId !== null || dismissingNotificationId !== null}
                    onClick={() => void viewInChat(item)}
                    aria-label={`View in chat for ${item.workflow_name}`}
                  >
                    {openingNotificationId === item.notification_id ? 'Opening…' : 'View in chat'}
                  </Button>
                ) : null}
                {canContinueInChat(item) && onOpenContinuedChat ? (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={openingNotificationId !== null || dismissingNotificationId !== null}
                    onClick={() => void continueInChat(item)}
                    aria-label={`Continue in chat for ${item.workflow_name}`}
                  >
                    {openingNotificationId === item.notification_id ? 'Opening…' : 'Continue in chat'}
                  </Button>
                ) : null}
                {targetIsReady(item) && !(canContinueInChat(item) && onOpenContinuedChat) ? (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={openingNotificationId !== null || dismissingNotificationId !== null}
                    onClick={() => void openStage(item, item.kind !== 'questions')}
                    aria-label={`Open stage for ${item.workflow_name}`}
                  >
                    {openingNotificationId === item.notification_id
                      ? 'Opening…'
                      : 'Open stage'}
                  </Button>
                ) : null}
                {targetIsReady(item) && canContinueInChat(item) && onOpenContinuedChat ? (
                  <Button size="small" onClick={() => void openStage(item, false)} aria-label={`Open workflow for ${item.workflow_name}`}>Open workflow</Button>
                ) : null}
                {!isExecutionActive(item) && !targetIsReady(item) && item.can_open_workflow === true ? (
                  <Button
                    size="small"
                    onClick={() => void openWorkflowFallback(item)}
                    aria-label={`Open workflow for ${item.workflow_name}`}
                  >
                    {openingNotificationId === item.notification_id ? 'Opening…' : 'Open workflow'}
                  </Button>
                ) : null}
                {!isExecutionActive(item)
                  && !targetIsReady(item)
                  && item.can_open_workflow !== true
                  && !(canContinueInChat(item) && onOpenContinuedChat) ? (
                    <Typography variant="body2" color="text.secondary">
                      This workflow is no longer at the notified stage.
                    </Typography>
                  ) : null}
                {(item.status === 'unread' || item.status === 'read') && (
                  <Button size="small" disabled={openingNotificationId !== null || dismissingNotificationId !== null} onClick={() => void dismissItem(item)} aria-label={`Dismiss notification for ${item.workflow_name}`}>Dismiss</Button>
                )}
              </Stack>}
            </ListItem>
          ))}
        </List>
      ) : null}
      {!loading && !loadError && hasMore && (
        <Box sx={{ pt: 1 }}>
          {pageError && <Typography role="alert">Unable to load more notifications right now. Please try again.</Typography>}
          <Button onClick={() => void load(nextOffset.current)} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more notifications'}
          </Button>
        </Box>
      )}
    </Box>
  );

  if (mobile) {
    return <Drawer anchor="bottom" open={open} onClose={onClose} PaperProps={{ role: 'dialog', 'aria-label': title }}>{content}</Drawer>;
  }
  // Workflow-card/table badges can sit beneath the fixed application header.
  // Their inbox therefore belongs in a viewport-centered modal, not an
  // anchor-positioned popover. The Account inbox remains a compact popover.
  if (flowId) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby="workflow-notifications-title"
        PaperProps={{ role: 'dialog', 'aria-label': title, 'aria-modal': true }}
      >
        {content}
      </Dialog>
    );
  }
  return (
    <Popover
      open={open}
      anchorReference={anchorEl ? 'anchorEl' : 'anchorPosition'}
      anchorEl={anchorEl ?? undefined}
      anchorPosition={anchorEl ? undefined : { top: 24, left: 24 }}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ role: 'dialog', 'aria-label': title }}
    >
      <Paper elevation={0}>{content}</Paper>
    </Popover>
  );
};

export default WorkflowNotificationsPanel;
